import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  type Dispatch,
  type SetStateAction,
  useMemo,
} from "react";
import { DateTime } from "luxon";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import {
  type Exception,
  type GroupedExceptionData,
  SessionDataGrid,
} from "@/utils/types";
import { customFirestore } from "@/firebase";
import { AuthContext } from "@/auth/AuthContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import { useAtomValue } from "jotai";
import { companyDataAtom } from "@/routes/test-run/test-run-atoms";
import {
  getSessionReviewBadge,
  getSessionReviewLabel,
  SESSION_REVIEW_FILTERS,
  sessionMatchesReviewFilter,
} from "./auto-review";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  DataGridPro,
  gridClasses,
  type GridColDef,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import CustomNoRowsOverlay from "@/components/custom-now-rows-overlay";
import { PieChart } from "@mui/x-charts-pro/PieChart";
import ExceptionsSparkline from "@/routes/sessions/exceptions-sparkline";
import { ChartsWrapper } from "./styled-components";

const SessionsPage = () => {
  useDocumentTitle("Sessions");
  const { user } = useContext<any>(AuthContext);
  const [loading, setLoading] = useState(true);
  const [loadingExceptions, setLoadingExceptions] = useState(true);
  const [data, setData] = useState<SessionDataGrid[]>([]);
  const [exceptions, setExceptions] = useState<GroupedExceptionData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [date, setDate] = useState<Date>(
    DateTime.now().minus({ week: 1 }).startOf("day").toJSDate()
  );

  useEffect(() => {
    const firebaseQuery = query(
      collection(customFirestore, `sessions`),
      where("organisationId", "==", user.companyId),
      where("createdAt", ">=", date),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(firebaseQuery, (snapshot) => {
      const sessions = snapshot.docs.map((session) => {
        const autoReview = session.data().autoReview;
        return {
          id: session.id,
          createdAt: DateTime.fromMillis(
            session.data().createdAt.toMillis()
          ).toJSDate(),
          platform: session.data().deviceConfig.platform,
          os: session.data().deviceConfig.os,
          device: session.data().deviceConfig.device,
          status: session.data().status,
          testId: session.data().testId ?? undefined,
          autoReview,
          autoReviewLabel: getSessionReviewLabel(autoReview),
        };
      }) as SessionDataGrid[];

      setLoading(false);
      setData(sessions);
    });
    return () => {
      unsubscribe();
    };
  }, [user.companyId, date]);

  useEffect(() => {
    const firebaseQuery = query(
      collection(customFirestore, `exceptions`),
      where("organisationId", "==", user.companyId),
      where("timestamp", ">=", date.getTime()),
      orderBy("timestamp", "desc")
    );
    const unsubscribe = onSnapshot(firebaseQuery, (snapshot) => {
      const exceptions = snapshot.docs.map((exception) => ({
        ...exception.data(),
        id: exception.id,
      })) as Exception[];

      const grouped = exceptions.reduce<Record<string, GroupedExceptionData>>(
        (groups, exception) => {
          const key = `${exception.filename}|${exception.class}|${exception.line}|${exception.method}`;

          if (!groups[key]) {
            groups[key] = {
              id: key,
              filename: exception.filename,
              class: exception.class,
              line: exception.line,
              method: exception.method,
              count: 0,
              occurrences: [],
            };
          }

          groups[key].count++;
          groups[key].occurrences.push(exception);
          groups[key].lastSeenAt = DateTime.fromMillis(
            exception.timestamp
          ).toJSDate();

          return groups;
        },
        {}
      );

      setLoadingExceptions(false);
      setExceptions(Object.values(grouped));
    });
    return () => {
      unsubscribe();
    };
  }, [user.companyId, date]);

  return (
    <div className="flex-1 flex flex-col bg-[#D9D9D9] h-full">
      <div className="flex flex-row flex-1 h-[calc(100vh_-_60px)]">
        {loading && (
          <div
            className={
              "flex flex-col h-full items-center justify-center w-full"
            }
          >
            <CircularProgress />
          </div>
        )}
        {error && <p>Error: {error}</p>}
        {!loading && !error && data != null && (
          <ReportSuccess
            date={date}
            setDate={setDate}
            sessions={data}
            exceptions={exceptions}
          />
        )}
      </div>
    </div>
  );
};

function ReportSuccess({
  sessions,
  date,
  exceptions,
  setDate,
}: {
  sessions: SessionDataGrid[];
  date: Date;
  setDate: Dispatch<SetStateAction<Date>>;
  exceptions: GroupedExceptionData[];
}) {
  const chartValues = useMemo(() => {
    const sums = exceptions.reduce(
      (prevGroup, currentGroup) => {
        const state = currentGroup.occurrences.reduce(
          (prevException, currentException) => {
            const healed = currentException.healedLocator !== "None";

            return {
              healed: prevException.healed + (healed ? 1 : 0),
              nonHealed: prevException.nonHealed + (!healed ? 1 : 0),
            };
          },
          { healed: 0, nonHealed: 0 }
        );
        return {
          healed: prevGroup.healed + state.healed,
          nonHealed: prevGroup.nonHealed + state.nonHealed,
        };
      },
      { healed: 0, nonHealed: 0 }
    );

    return Object.keys(sums).map((key) => ({
      id: key,
      label: key === "healed" ? "Healed" : "Non-healed",
      value: sums[key as keyof typeof sums],
      color: key === "healed" ? "rgb(34, 191, 117)" : "rgb(250, 79, 88)",
    }));
  }, [exceptions]);

  const sparklineData = useMemo(() => {
    const plainExceptions = exceptions
      .map((exception) => exception.occurrences)
      .flat();

    const weeklyExceptions = plainExceptions
      .toSorted((a, b) => {
        return a.timestamp - b.timestamp;
      })
      .reduce<Record<string, Exception[]>>((acc, curr) => {
        const weekStart = DateTime.fromMillis(curr.timestamp).startOf("week");
        const weekEnd = DateTime.fromMillis(curr.timestamp).endOf("week");
        const weekFormat = "yyyy-MM-dd";
        const weekNumber = `${weekStart.toFormat(
          weekFormat
        )} - ${weekEnd.toFormat(weekFormat)}`;

        acc[weekNumber] = [...(acc[weekNumber] ?? []), curr];
        return acc;
      }, {});

    return Object.entries(weeklyExceptions).map(([week, exceptions]) => ({
      exceptions: exceptions.filter((e) => e.healedLocator !== "None"),
      week,
    }));
  }, [exceptions]);

  const [activeTab, setActiveTab] = useState(0);
  const sessionsApiRef = useGridApiRef();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      if (sessionsApiRef.current) {
        sessionsApiRef.current.setQuickFilterValues(
          value ? value.split(" ") : []
        );
      }
    },
    [sessionsApiRef]
  );

  const handleSearchClose = useCallback(() => {
    setSearchOpen(false);
    handleSearchChange("");
  }, [handleSearchChange]);

  return (
    <ReportsSuccessPageWrapper>
      <ReportsSuccessPageHeader>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              minHeight: 40,
              textTransform: "none",
              fontWeight: 500,
              fontSize: "1rem",
            },
          }}
        >
          <Tab label="Sessions" />
          <Tab label="Exceptions" />
        </Tabs>
        {activeTab === 1 && exceptions.length > 0 && (
          <ChartsWrapper>
            <PieChart
              width={172}
              height={70}
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              series={[
                {
                  data: chartValues,
                  innerRadius: 20,
                  outerRadius: 60,
                  paddingAngle: 3,
                  cornerRadius: 5,
                  startAngle: -90,
                  endAngle: 90,
                  cx: 100,
                  cy: 65,
                },
              ]}
            />
            {sparklineData.length > 1 && (
              <ExceptionsSparkline data={sparklineData} />
            )}
          </ChartsWrapper>
        )}
        <DatePicker
          label={"From Date"}
          value={DateTime.fromJSDate(date)}
          onChange={(value) => {
            setDate(value!.toJSDate());
          }}
          sx={{ marginLeft: "auto" }}
        />
      </ReportsSuccessPageHeader>
      <TabContentWrapper>
        {activeTab === 0 && (
          <Sessions
            sessions={sessions}
            apiRef={sessionsApiRef}
            searchOpen={searchOpen}
            searchValue={searchValue}
            onSearchOpen={() => setSearchOpen(true)}
            onSearchChange={handleSearchChange}
            onSearchClose={handleSearchClose}
          />
        )}
        {activeTab === 1 && <Exceptions exceptions={exceptions} date={date} />}
      </TabContentWrapper>
    </ReportsSuccessPageWrapper>
  );
}

const Sessions = ({
  sessions,
  apiRef,
  searchOpen,
  searchValue,
  onSearchOpen,
  onSearchChange,
  onSearchClose,
}: {
  sessions: SessionDataGrid[];
  apiRef: ReturnType<typeof useGridApiRef>;
  searchOpen: boolean;
  searchValue: string;
  onSearchOpen: () => void;
  onSearchChange: (value: string) => void;
  onSearchClose: () => void;
}) => {
  const { user } = useContext<any>(AuthContext);
  const isSuperUser = user?.role === "superUser";
  const { data: companyData } = useAtomValue(companyDataAtom(user?.companyId));
  const reviewEnabled =
    companyData?.settings?.enableSdkSessionsAutoReview === true;
  // Review UI is superuser-only AND only for orgs where the feature is enabled.
  const showReviewUi = isSuperUser && reviewEnabled;

  const getSessionUrl = useCallback(
    (id: string) => `/gpt-driver/sessions/${id}`,
    []
  );

  const [reviewFilter, setReviewFilter] = useState("all");
  const filteredSessions = useMemo(
    () =>
      (sessions ?? []).filter((session) =>
        sessionMatchesReviewFilter(session.autoReview, reviewFilter)
      ),
    [sessions, reviewFilter]
  );

  const columns = useMemo(() => {
    const cellLink = (params: any, content: React.ReactNode) => (
      <Link
        to={getSessionUrl(params.row.id)}
        style={{
          color: "inherit",
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      >
        {content}
      </Link>
    );

    return [
      {
        field: "createdAt",
        headerName: "Time",
        filterable: false,
        sortable: true,
        type: "dateTime",
        flex: 1,
        renderCell: (params: any) =>
          cellLink(
            params,
            DateTime.fromJSDate(params.value).toFormat("yyyy-MM-dd hh:mm a")
          ),
      },
      {
        field: "platform",
        headerName: "Platform",
        filterable: true,
        sortable: true,
        flex: 0.7,
        renderCell: (params: any) => cellLink(params, params.value),
      },
      {
        field: "os",
        headerName: "OS",
        filterable: true,
        sortable: true,
        flex: 0.5,
        renderCell: (params: any) => cellLink(params, params.value),
      },
      {
        field: "device",
        headerName: "Device",
        filterable: true,
        sortable: true,
        flex: 0.7,
        renderCell: (params: any) => cellLink(params, params.value),
      },
      {
        field: "status",
        headerName: "Status",
        filterable: true,
        sortable: true,
        type: "string",
        flex: 0.7,
        renderCell: (params: any) => cellLink(params, params.value),
      },
      // Auto-review column: superuser-only AND only when the org flag is on.
      ...(showReviewUi
        ? [
            {
              field: "autoReviewLabel",
              headerName: "Auto-review",
              filterable: true,
              sortable: true,
              type: "string",
              flex: 1,
              renderCell: (params: any) => {
                const badge = getSessionReviewBadge(params.row.autoReview);
                if (!badge) {
                  return cellLink(params, "-");
                }
                const chip = (
                  <Chip
                    size="small"
                    color={badge.color}
                    label={badge.label}
                    sx={{ height: 20 }}
                  />
                );
                return cellLink(
                  params,
                  badge.tooltip ? (
                    <Tooltip title={badge.tooltip}>{chip}</Tooltip>
                  ) : (
                    chip
                  )
                );
              },
            },
          ]
        : []),
      {
        field: "testId",
        headerName: "Test ID",
        filterable: true,
        sortable: true,
        flex: 1.5,
        renderCell: (params: any) => cellLink(params, params.value ?? "-"),
      },
    ];
  }, [getSessionUrl, showReviewUi]);

  return (
    <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
      <SearchOverlay>
        {showReviewUi && (
          <FormControl
            size="small"
            sx={{ minWidth: 170, backgroundColor: "white" }}
          >
            <InputLabel id="auto-review-filter-label">Auto-review</InputLabel>
            <Select
              labelId="auto-review-filter-label"
              label="Auto-review"
              value={reviewFilter}
              onChange={(e) => setReviewFilter(e.target.value)}
            >
              {SESSION_REVIEW_FILTERS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {searchOpen ? (
          <TextField
            autoFocus
            size="small"
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={onSearchClose}>
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: 220, backgroundColor: "white" }}
          />
        ) : (
          <IconButton size="small" onClick={onSearchOpen}>
            <Search />
          </IconButton>
        )}
      </SearchOverlay>
      <DataGridPro
        apiRef={apiRef}
        rows={filteredSessions}
        initialState={{
          sorting: { sortModel: [{ field: "createdAt", sort: "desc" }] },
        }}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        showCellVerticalBorder={false}
        showColumnVerticalBorder={false}
        disableColumnMenu={true}
        className={"bg-white"}
        columns={columns}
        slots={{ noRowsOverlay: CustomNoRowsOverlay }}
        slotProps={{
          noRowsOverlay: { title: "No sessions yet" },
        }}
        isRowSelectable={(row) => false}
        sx={{
          "& .MuiDataGrid-main": {
            paddingLeft: "8px",
          },
          [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
            outline: "transparent",
          },
          [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
            {
              outline: "none",
            },
          [`& .${gridClasses.row}:hover`]: {
            cursor: "pointer",
          },
        }}
      />
    </div>
  );
};

const Exceptions = ({
  exceptions,
  date,
}: {
  exceptions: GroupedExceptionData[];
  date: Date;
}) => {
  const getExceptionUrl = useCallback(
    (row: GroupedExceptionData) =>
      `/gpt-driver/exceptions?class=${row.class}&method=${row.method}&line=${row.line}&from=${date.getTime()}&filename=${row.filename}`,
    [date]
  );

  const columns: GridColDef<GroupedExceptionData>[] = useMemo(() => {
    const cellLink = (params: any, content: React.ReactNode) => (
      <Link
        to={getExceptionUrl(params.row)}
        style={{
          color: "inherit",
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      >
        {content}
      </Link>
    );

    return [
      {
        field: "filename",
        headerName: "File Name",
        filterable: false,
        sortable: true,
        flex: 1,
        renderCell: (params: any) => cellLink(params, params.value),
      },
      {
        field: "line",
        headerName: "Line",
        filterable: true,
        sortable: true,
        flex: 0.5,
        renderCell: (params: any) => cellLink(params, params.value),
      },
      {
        field: "class",
        headerName: "Class",
        filterable: false,
        sortable: true,
        flex: 1,
        renderCell: (params: any) => cellLink(params, params.value),
      },
      {
        field: "method",
        headerName: "Method",
        filterable: true,
        sortable: true,
        flex: 1,
        renderCell: (params: any) => cellLink(params, params.value),
      },
      {
        field: "occurrences",
        headerName: "Occurrences",
        type: "custom",
        filterable: true,
        sortable: true,
        flex: 0.7,
        renderCell: (params: any) =>
          cellLink(params, params.value?.length ?? 0),
        sortComparator: (v1, v2) => v1.length - v2.length,
      },
      {
        field: "lastSeenAt",
        headerName: "Last Seen",
        type: "dateTime",
        filterable: true,
        sortable: true,
        flex: 1,
        renderCell: (params: any) =>
          cellLink(
            params,
            DateTime.fromJSDate(params.value).toFormat("yyyy-MM-dd hh:mm a")
          ),
      },
    ];
  }, [getExceptionUrl]);

  return (
    <DataGridPro
      rows={exceptions ?? []}
      initialState={{
        sorting: {
          sortModel: [
            { field: "occurrences", sort: "desc" },
            { field: "lastSeenAt", sort: "desc" },
          ],
        },
      }}
      showCellVerticalBorder={false}
      showColumnVerticalBorder={false}
      slots={{ noRowsOverlay: CustomNoRowsOverlay }}
      slotProps={{
        noRowsOverlay: { title: "No self healed exceptions yet" },
      }}
      disableColumnMenu={true}
      className={"bg-white"}
      columns={columns}
      isRowSelectable={(row) => false}
      sx={{
        "& .MuiDataGrid-main": {
          paddingLeft: "8px",
        },
        [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
          outline: "transparent",
        },
        [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
          {
            outline: "none",
          },
        [`& .${gridClasses.row}:hover`]: {
          cursor: "pointer",
        },
      }}
    />
  );
};

const SearchOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 8px;
  height: 56px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 5;
  pointer-events: auto;
`;

const TabContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const ReportsSuccessPageHeader = styled.div`
  grid-area: header;
  display: flex;
  justify-content: space-between;
  padding-right: 4px;
`;

const ReportsSuccessPageWrapper = styled.div`
  display: grid;
  grid-template-areas:
    "header"
    "sessions";
  width: 100%;
  grid-template-rows: auto minmax(0, 1fr);
  padding: 16px;
  margin: 16px;
  background-color: #e8e8e8;
  border-radius: 8px;
  gap: 24px;
`;

export default SessionsPage;
